import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { MapPin, Settings } from 'lucide-react';
import TenantAIKeyManagement from './TenantAIKeyManagement';
import { getErrorMessage } from '../../utils/errorHelpers';

const TenantStateManagement = () => {
  const [tenants, setTenants] = useState([]);
  const [allStates, setAllStates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingTenant, setEditingTenant] = useState(null);
  const [selectedTenantForKeys, setSelectedTenantForKeys] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const headers = {
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      };
      
      const [tenantsRes, statesRes] = await Promise.all([
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/admin/tenants`, { headers }),
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/admin/states`, { headers })
      ]);
      
      console.log('Fetched tenants:', tenantsRes.data);
      console.log('Fetched states:', statesRes.data.states);
      
      setTenants(tenantsRes.data || []);
      setAllStates(statesRes.data.states || []);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateTenantStates = async (tenantId, enabledStates) => {
    console.log('Updating tenant states:', { tenantId, enabledStates });
    
    try {
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/tenants/${tenantId}/states`,
        { enabled_states: enabledStates },
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      
      await fetchData();
      setEditingTenant(null);
      
      alert('Tenant states updated successfully!');
    } catch (error) {
      console.error('Error updating tenant states:', error);
      console.error('Error details:', error.response?.data);
      const errorMsg = getErrorMessage(error, 'Failed to update tenant states');
      alert(`Failed to update tenant states: ${errorMsg}`);
    }
  };

  if (loading) {
    return <div className="p-6">Loading...</div>;
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <h1 className="text-3xl font-bold mb-6">Tenant State & AI Management</h1>
      
      {/* Available States Overview */}
      <div className="mb-8 p-4 bg-blue-50 rounded-lg">
        <h2 className="text-xl font-semibold mb-3 flex items-center">
          <MapPin className="w-5 h-5 mr-2" />
          Available States
        </h2>
        <div className="flex flex-wrap gap-3">
          {allStates.map(state => (
            <div
              key={state.code}
              className={`px-4 py-2 rounded-lg ${
                state.enabled 
                  ? 'bg-green-100 text-green-800' 
                  : 'bg-gray-100 text-gray-600'
              }`}
            >
              <span className="font-semibold">{state.code}</span> - {state.name}
              {!state.enabled && <span className="ml-2 text-xs">(Coming Soon)</span>}
            </div>
          ))}
        </div>
      </div>

      {/* Tenants List */}
      <div className="space-y-4">
        {tenants.map(tenant => (
          <div key={tenant.id} className="border rounded-lg p-6 bg-white shadow">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="text-xl font-semibold">{tenant.name}</h3>
                <p className="text-gray-600">{tenant.admin_email || tenant.email}</p>
                <span className="inline-block mt-2 px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">
                  {tenant.plan}
                </span>
              </div>
              
              <div className="flex gap-2">
                <button
                  onClick={() => setEditingTenant(tenant.id)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center"
                >
                  <MapPin className="w-4 h-4 mr-2" />
                  Edit States
                </button>
                
                <button
                  onClick={() => setSelectedTenantForKeys({
                    id: tenant.id,
                    name: tenant.name
                  })}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center"
                >
                  <Settings className="w-4 h-4 mr-2" />
                  AI Keys
                </button>
              </div>
            </div>

            {/* Current States */}
            <div className="mb-3">
              <h4 className="font-semibold mb-2 text-sm text-gray-700">Enabled States:</h4>
              <div className="flex flex-wrap gap-2">
                {tenant.enabled_states?.length > 0 ? (
                  tenant.enabled_states.map(stateCode => {
                    const state = allStates.find(s => s.code === stateCode);
                    return state ? (
                      <span
                        key={stateCode}
                        className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm"
                      >
                        {state.code} - {state.name}
                      </span>
                    ) : null;
                  })
                ) : (
                  <span className="text-gray-500 text-sm">No states enabled</span>
                )}
              </div>
            </div>

            {/* AI Configuration Summary */}
            <div className="mt-3 p-3 bg-gray-50 rounded">
              <p className="text-sm">
                <span className="font-semibold">AI Setup:</span>{' '}
                {tenant.ai_config?.key_strategy === 'platform_shared' && (
                  <span className="text-gray-600">Using platform shared key</span>
                )}
                {tenant.ai_config?.key_strategy === 'tenant_dedicated' && (
                  <span className="text-green-600">Has dedicated key ({tenant.ai_config.ai_provider})</span>
                )}
                {tenant.ai_config?.key_strategy === 'byok' && (
                  <span className="text-blue-600">Using customer's own key ({tenant.ai_config.ai_provider})</span>
                )}
                {!tenant.ai_config?.key_strategy && (
                  <span className="text-gray-600">Using platform shared key (default)</span>
                )}
              </p>
            </div>

            {/* Edit Modal */}
            {editingTenant === tenant.id && (
              <StateSelectionModal
                tenant={tenant}
                allStates={allStates}
                onSave={(enabledStates) => updateTenantStates(tenant.id, enabledStates)}
                onCancel={() => setEditingTenant(null)}
              />
            )}
          </div>
        ))}
      </div>

      {/* AI Key Management Modal */}
      {selectedTenantForKeys && (
        <TenantAIKeyManagement
          tenantId={selectedTenantForKeys.id}
          tenantName={selectedTenantForKeys.name}
          onClose={() => {
            setSelectedTenantForKeys(null);
            fetchData();
          }}
        />
      )}
    </div>
  );
};

const StateSelectionModal = ({ tenant, allStates, onSave, onCancel }) => {
  const [selectedStates, setSelectedStates] = useState(tenant.enabled_states || []);
  const [searchTerm, setSearchTerm] = useState('');

  const toggleState = (stateCode) => {
    setSelectedStates(prev =>
      prev.includes(stateCode)
        ? prev.filter(s => s !== stateCode)
        : [...prev, stateCode]
    );
  };

  // Group states by region
  const statesByRegion = allStates.reduce((acc, state) => {
    const region = state.region || 'Other';
    if (!acc[region]) acc[region] = [];
    acc[region].push(state);
    return acc;
  }, {});

  const regionOrder = ['South', 'West', 'North', 'Central', 'East', 'Northeast', 'Other'];
  const sortedRegions = regionOrder.filter(r => statesByRegion[r]);

  // Filter states by search
  const filteredStates = searchTerm
    ? allStates.filter(s => 
        s.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        s.code.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : null;

  const selectAll = () => {
    setSelectedStates(allStates.map(s => s.code));
  };

  const selectNone = () => {
    setSelectedStates([]);
  };

  const selectRegion = (region) => {
    const regionCodes = statesByRegion[region]?.map(s => s.code) || [];
    setSelectedStates(prev => [...new Set([...prev, ...regionCodes])]);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <h3 className="text-xl font-bold mb-2">
          Edit States for {tenant.name}
        </h3>
        <p className="text-sm text-gray-600 mb-4">
          Select which Indian states this tenant can create legal opinions for.
          ({selectedStates.length} of {allStates.length} selected)
        </p>

        {/* Search and Quick Actions */}
        <div className="flex gap-2 mb-4">
          <input
            type="text"
            placeholder="Search states..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="flex-1 px-3 py-2 border rounded-lg text-sm"
          />
          <button
            onClick={selectAll}
            className="px-3 py-2 text-xs bg-green-100 text-green-700 rounded hover:bg-green-200"
          >
            All
          </button>
          <button
            onClick={selectNone}
            className="px-3 py-2 text-xs bg-red-100 text-red-700 rounded hover:bg-red-200"
          >
            None
          </button>
        </div>

        {/* States List */}
        <div className="flex-1 overflow-y-auto space-y-4 mb-4" style={{maxHeight: '50vh'}}>
          {filteredStates ? (
            // Search results
            <div className="space-y-2">
              {filteredStates.map(state => (
                <StateCheckbox
                  key={state.code}
                  state={state}
                  checked={selectedStates.includes(state.code)}
                  onToggle={() => toggleState(state.code)}
                />
              ))}
              {filteredStates.length === 0 && (
                <p className="text-gray-500 text-center py-4">No states match your search</p>
              )}
            </div>
          ) : (
            // Grouped by region
            sortedRegions.map(region => (
              <div key={region} className="border rounded-lg p-3">
                <div className="flex justify-between items-center mb-2">
                  <h4 className="font-semibold text-gray-700">{region} India</h4>
                  <button
                    onClick={() => selectRegion(region)}
                    className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                  >
                    Select All
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {statesByRegion[region].map(state => (
                    <StateCheckbox
                      key={state.code}
                      state={state}
                      checked={selectedStates.includes(state.code)}
                      onToggle={() => toggleState(state.code)}
                      compact
                    />
                  ))}
                </div>
              </div>
            ))
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3 pt-4 border-t">
          <button
            onClick={() => onSave(selectedStates)}
            className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Save Changes ({selectedStates.length} states)
          </button>
          <button
            onClick={onCancel}
            className="flex-1 px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

const StateCheckbox = ({ state, checked, onToggle, compact = false }) => (
  <label
    className={`flex items-center ${compact ? 'p-2' : 'p-3'} border rounded-lg cursor-pointer hover:bg-gray-50 ${
      checked ? 'border-blue-300 bg-blue-50' : 'border-gray-200'
    }`}
  >
    <input
      type="checkbox"
      checked={checked}
      onChange={onToggle}
      className={`${compact ? 'mr-2 h-4 w-4' : 'mr-3 h-5 w-5'}`}
    />
    <div className="flex-1 min-w-0">
      <div className={`font-medium ${compact ? 'text-sm' : ''} truncate`}>
        {state.code} - {state.name}
      </div>
      {!compact && (
        <div className="flex gap-2 text-xs text-gray-600">
          <span>{state.bank_formats?.length || 0} formats</span>
          {state.has_custom_schema && (
            <span className="px-1 bg-green-100 text-green-700 rounded">Custom Schema</span>
          )}
        </div>
      )}
    </div>
  </label>
);

export default TenantStateManagement;
